import React, { useMemo, useState, useEffect } from 'react';
import Filter from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/components/Filter';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import {
  fetchStoreRanksDetailsRankData,
  fetchStoreRanksDetailsTrendData,
} from '@/pages/intelligence/services/storeRank';
import { dbiPageLogMes } from '@/pages/intelligence/utils/buriedPointCfg';
import { getInitialFetchParamsDetailStoreRank } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/utils';
import {
  MOBILE_SORT_FREE_TOPS_KEYS,
  MOBILE_SORT_PAID_TOPS_KEYS,
  MOBILE_SORT_FREE_TOPS_SEGMENT,
  MOBILE_SORT_PAID_TOPS_SEGMENT,
} from '@/pages/intelligence/_config/storeRank/config';
import '@/pages/intelligence/assets/scss/TopCharts/MobileGames/components/StoreRank.scss';
import type { IBaseProps, IGameType, appIdObjType } from '@/pages/intelligence/types/common';
import type {
  IStoreRankDetailsDataFetchParams,
  IStoreRankDetailsRankTrendParams,
} from '@/pages/intelligence/types/storeRank';
import ExportTable from '@/pages/intelligence/components/ExportTable';
import { STATIC_FILTER_CONFIGS } from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/components/Filter/config';
import RankTrendVisualis from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/RankTrend/components/RankTrendVisualis';
import RankTrendTableContent from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/RankTrend/components/RankTrendTableContent';
import useRankTrendModel from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/StoreRank/RankTrend/hooks/useRankTrendModel';
import { buriedPointGame } from '@/pages/home/utils/buriedPoint';
import { useSelector, useDispatch } from 'umi';
import useEarliestTime from '@/pages/intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utilsTmp/useEarliestTime';
import { DBI_TOPCHARTS } from '@/pages/intelligence/models/topcharts';
import { toDateTypeNor } from '@/pages/intelligence/utils/common';
import { getFirstWeekSinceReleaseDates, isFirstWeekSinceReleaseShortcut } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/utils';

const DEFAULT_TRENDDATA_PARAMS: IStoreRankDetailsRankTrendParams = {
  entity_type: 'mobile',
  app_ids: [],
  source: MOBILE_SORT_FREE_TOPS_KEYS,
  region_type: 'market',
  regions: ['global'],
  granularity: 'daily',
  start_date: '',
  end_date: '',
  order_source: 'appstore top paid',
  order: 'desc',
  ratio_type: 1,
};

interface IStoreRankDetailsProps extends IBaseProps {
  entityType: IGameType;
  appIds?: any;
  isBasicLoading: boolean;
  basicInfo: object | any;
  activeSegment: string;
  setActiveSegment?: any;
}

const InputList = [
  STATIC_FILTER_CONFIGS.SEGMENT_STORERANK,
  STATIC_FILTER_CONFIGS.RANGE_TIME_SELECT_FILTER,
  STATIC_FILTER_CONFIGS.COUNTRY_REGION_FILTER,
];

const RankTrend: React.FC<IStoreRankDetailsProps> = (props) => {
  const {
    entityType,
    appIds: appIdsProps,
    isBasicLoading,
    basicInfo = {},
    activeSegment,
    setActiveSegment,
    appIdListAll = [],
    filterLoading,
    regionOptions,
  } = props;
  // console.log('RankTrendRankTrend', props);
  const dispatch = useDispatch();
  // 判断此游戏是否用免费榜单
  const isFreeCur = useMemo(() => {
    const appIdsList = appIdListAll?.filter((item: appIdObjType) => appIdsProps?.includes(item.id));
    return appIdsList?.findIndex((item: appIdObjType) => item.is_free === true) > -1;
  }, [appIdListAll, appIdsProps]);

  // 根据游戏是否免费,获取相应榜单
  const sourceCur = useMemo(
    () => (isFreeCur ? MOBILE_SORT_FREE_TOPS_KEYS : MOBILE_SORT_PAID_TOPS_KEYS),
    [isFreeCur],
  );
  const [rankType, setRankType] = useState('avg_ranking');
  // 根据游戏是否免费,获取相应榜单segment
  const segmentListCur = useMemo(
    () => (isFreeCur ? MOBILE_SORT_FREE_TOPS_SEGMENT : MOBILE_SORT_PAID_TOPS_SEGMENT),
    [isFreeCur],
  );

  const [segmentValue, setSegmentValue] = useState(
    segmentListCur?.length > 0 ? segmentListCur[0]?.value : '',
  );
  const [earliestPeriodCur, setEarliestPeriodCur] = useState('');

  const topchartsState = useSelector((state: any) => state?.[DBI_TOPCHARTS] || {});
  const appIds = useMemo(() => {
    if (!Array.isArray(appIdsProps)) return [];
    return appIdsProps?.filter((id) => id?.length > 0);
  }, [appIdsProps]);
  const { isOperateDetail, detailFilterExtend } = useFilterExtend({});

  const defaultFetchParams = {
    ...DEFAULT_TRENDDATA_PARAMS,
    app_ids: appIds,
    source: sourceCur,
  };

  // 经过继承后，得到的访问页面接口参数
  const initialFetchParams = getInitialFetchParamsDetailStoreRank(
    isOperateDetail,
    detailFilterExtend,
    defaultFetchParams,
    activeSegment,
  );

  const { updateEarliestDateData, baseReleaseTimeValue, earliestDayData } = useEarliestTime({
    entityType,
    basicInfo,
    topchartsState,
    segmentValue,
    setEarliestPeriodCur,
  });

  const {
    fetchParams,
    setFetchParams,
    tableTotal,
    tableData,
    lineData,
    trendLoading,
    trendError,
    getTableData,
    defaultSelectionGames,
    isTableNodata,
    isLineNodata,
    tableSelection,
    getLineChartData,
    setTableSelection,
    tableLoading,
    changeMarketProfileAll,
    updateTableField,
    onSortChage,
    pageSortAttr,
    tableSearchValue,
    resetPageSortAttr,
    setIsOperateCheckbox,
    reqDateObject,
    setBoolean,
  } = useRankTrendModel({
    defaultParams: initialFetchParams,
    getTableApiFun: fetchStoreRanksDetailsRankData,
    getTrendApiFun: fetchStoreRanksDetailsTrendData,
    isBasicLoading,
    basicInfo,
    isFreeCur,
    appIdListAll,
    regionOptions,
    rankType,
    updateEarliestDateData,
  });

  const handleMetricChangeFun = (value: string) => {
    const earliestDayList = [...earliestDayData]?.filter((item: any) => item.source === value);
    if (earliestDayList?.length === 0) {
      dispatch({ type: `${DBI_TOPCHARTS}/setEarliestCurDay`, payload: '' });
      setEarliestPeriodCur('');
    } else {
      for (const n in earliestDayList) {
        if (toDateTypeNor[fetchParams?.granularity] === toDateTypeNor[earliestDayList[n]?.period]) {
          dispatch({
            type: `${DBI_TOPCHARTS}/setEarliestCurDay`,
            payload: earliestDayList[n]?.date || '',
          });
          setEarliestPeriodCur(earliestDayList[n].date || '');
        }
      }
    }
  };

  const handleConfirm = (
    payload: Partial<IStoreRankDetailsDataFetchParams>,
    segmentValue: string,
  ) => {
    const fetchParamsTmp = {
      ...fetchParams,
      ...payload,
      // regions: getMarketsValue(payload.regions, regionOptions),
    };
    if (segmentValue === 'rank') {
      getTableData(fetchParamsTmp);
      setFetchParams(fetchParamsTmp);
    }
  };

  const handleReset = () => {};

  useEffect(() => {
    const pageLogConfig = dbiPageLogMes?.mobileGameDetailsStoreRankRankTrend;
    if (!pageLogConfig?.pageId) return;
    buriedPointGame(pageLogConfig, props?.gameId, 'edition_id');
  }, []);
  useEffect(() => {
    if (isBasicLoading) return;
    if (filterLoading) return;
    if (activeSegment === 'rank') {
      getTableData(fetchParams);
    }
  }, [isBasicLoading, filterLoading]);

  const downloadRegions = useMemo(() => {
    if (Array.isArray(tableData) && tableData?.length > 0) {
      return (
        tableData
          ?.filter((item: any) => Array.isArray(item?.metrics) && item?.metrics?.length > 0)
          ?.map((item: any) => item?.market) || []
      );
    }
    return fetchParams?.regions;
  }, [tableData]);

  const downloadTableParams = useMemo(
    () => ({
      entity_type: entityType,
      app_ids: appIds,
      date_type: fetchParams?.granularity,
      region_type: fetchParams?.region_type,
      regions: downloadRegions,
      start_date: fetchParams?.start_date,
      end_date: fetchParams?.end_date,
      unified_id: basicInfo?.id,
      current_tab: rankType === 'avg_ranking' ? 'avg' : 'latest',
      source: fetchParams?.source,
    }),
    [fetchParams, tableData, basicInfo, rankType],
  );

  const firstWeekSinceReleaseDates = useMemo(
    () => {
      if (!isFirstWeekSinceReleaseShortcut(topchartsState?.selectedTimeShortcutType)) {
        return [];
      }
      return getFirstWeekSinceReleaseDates({
        dateType: fetchParams?.granularity,
        startDate: fetchParams?.start_date,
        endDate: fetchParams?.end_date,
        releaseDate: baseReleaseTimeValue,
      });
    },
    [
      topchartsState?.selectedTimeShortcutType,
      fetchParams?.granularity,
      fetchParams?.start_date,
      fetchParams?.end_date,
      baseReleaseTimeValue,
    ],
  );
  return (
    <div className="dbi-mobile-games-storerank dbi-mobile-games-details-store-rank">
      <div className="dbi-statsfilter-warp">
        <div className="statsfilter-warp-left">
          {/* RankTrend筛选器内容 */}
          <Filter
            regionsOptions={regionOptions}
            source={fetchParams?.source?.[0]}
            granularity={fetchParams?.granularity}
            startDate={fetchParams?.start_date}
            endDate={fetchParams?.end_date}
            onConfirm={handleConfirm}
            onReset={handleReset}
            inputList={InputList}
            setActiveSegment={setActiveSegment}
            activeSegment={activeSegment}
            filterLoading={filterLoading}
            regionOptions={regionOptions}
            sinceReleaseTime={baseReleaseTimeValue}
            earliestDataTime={earliestPeriodCur}
          />
        </div>
        <div className="dbi-statsfilter-warp-right">
          {/* RankTrend下载内容 */}
          <ExportTable
            entityType={entityType}
            tableParams={downloadTableParams}
            downloadLogConfigKey="mobileGameDetailsStoreRankRankTrendDownloadEChart"
            isShowMaxDownloadRowsTips={false}
            businessType="mobile-details-store-rank-trend"
            downloadModalSize="l"
            isDisableDownload={tableLoading}
            isDisableDataAmount={true}
            total={tableTotal}
            newStyleFileName={`${basicInfo?.name}_Countries(${tableTotal}) Store Rank`}
          />
        </div>
      </div>
      {/* RankTrend可视化内容 */}
      <RankTrendVisualis
        lineData={lineData}
        loading={isBasicLoading}
        trendLoading={trendLoading}
        trendError={trendError}
        entityType={entityType}
        tableSelection={tableSelection}
        segmentListCur={segmentListCur}
        isFreeCur={isFreeCur}
        filterLoading={filterLoading}
        regionOptions={regionOptions}
        isLineNodata={isLineNodata}
        appIds={appIds}
        segmentValue={segmentValue}
        setSegmentValue={setSegmentValue}
        handleMetricChangeFun={handleMetricChangeFun}
        firstWeekSinceReleaseDates={firstWeekSinceReleaseDates}
      />
      {/* RankTrend宽表内容 */}
      <RankTrendTableContent
        entityType={entityType}
        tableData={tableData}
        appIdListAll={appIdListAll}
        getTableData={getTableData}
        tableSelection={tableSelection}
        setTableSelection={setTableSelection}
        changeMarketProfileAll={changeMarketProfileAll}
        selectAllKeys={defaultSelectionGames}
        setTableSearchValue={updateTableField}
        onSortChage={onSortChage}
        pageSortAttr={pageSortAttr}
        tableSearchValue={tableSearchValue}
        resetPageSortAttr={resetPageSortAttr}
        fetchParams={fetchParams}
        tableLoading={tableLoading}
        setIsOperateCheckbox={setIsOperateCheckbox}
        filterLoading={filterLoading}
        regionOptions={regionOptions}
        getLineChartData={getLineChartData}
        rankType={rankType}
        setRankType={setRankType}
        isTableNodata={isTableNodata}
        appIdsProps={appIdsProps}
        isFreeCur={isFreeCur}
        reqDateObject={reqDateObject}
        basicInfo={basicInfo}
        setBoolean={setBoolean}
      />
    </div>
  );
};

export default RankTrend;
